import React, { useState } from 'react';
import { Card, Space, Typography, Button, message, Tooltip, Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileOutlined,
  FileTextOutlined,
  FileZipOutlined,
  EyeOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { Message } from '../../types/chat';
import { isValidFileInfo, truncateFileName } from './utils';
import { BASE_URL } from '../../config';
import ImageLightbox from './ImageLightbox';

const { Text, Title } = Typography;

interface FileAttachmentProps {
  fileInfo: NonNullable<Message['fileInfo']>;
}

const FileAttachment: React.FC<FileAttachmentProps> = ({ fileInfo }) => {
  const [showLightbox, setShowLightbox] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const { t } = useTranslation();
  
  // Get file icon based on mimetype
  const getFileIcon = (mimetype: string) => {
    if (mimetype.startsWith('image/')) return <FileImageOutlined />;
    if (mimetype.includes('pdf')) return <FilePdfOutlined />;
    if (mimetype.includes('zip') || mimetype.includes('rar') || mimetype.includes('tar') || mimetype.includes('gz')) return <FileZipOutlined />;
    if (mimetype.includes('text') || mimetype.includes('doc') || mimetype.includes('office')) return <FileTextOutlined />;
    return <FileOutlined />;
  };

  // Handle file download
  const handleFileDownload = async () => {
    if (isDownloading) return;
    
    setIsDownloading(true);
    setDownloadProgress(0);
    
    try {
      // For all file types, fetch the file as blob
      const response = await fetch(`${BASE_URL}${fileInfo.pathToFile}`);
      if (!response.ok) throw new Error('Ошибка загрузки файла');
      
      // Get total size for progress calculation
      const totalBytes = Number(response.headers.get('content-length')) || 0;
      const reader = response.body?.getReader();
      
      if (!reader) {
        throw new Error('Не удалось инициализировать чтение файла');
      }
      
      // Read the data chunks and track progress
      let receivedBytes = 0;
      const chunks: Uint8Array[] = [];
      
      while(true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }
        
        chunks.push(value);
        receivedBytes += value.length;
        
        if (totalBytes) {
          setDownloadProgress(Math.round((receivedBytes / totalBytes) * 100));
        }
      }
      
      // Combine all chunks into a blob
      const blob = new Blob(chunks);
      
      // Create object URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create an invisible link and trigger download
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileInfo.filename || 'download';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Ошибка скачивания:', error);
      message.error('Не удалось скачать файл');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Handle image click
  const handleImageClick = () => {
    setShowLightbox(true);
  };

  // Close lightbox
  const closeLightbox = () => {
    setShowLightbox(false);
  };

  // If it's an image
  if (fileInfo.mimetype.startsWith('image/')) {
    return (
      <>
        <Card 
          className="file-attachment-card" 
          variant="borderless"
          styles={{ body: { padding: 0 } }}
          style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}
        >
          <div className="image-preview-container" style={{ position: 'relative' }}>
            <div 
              style={{ 
                cursor: 'pointer',
                overflow: 'hidden',
                borderRadius: '8px 8px 0 0',
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#f0f2f5'
              }}
              onClick={handleImageClick}
            >
              <img 
                src={`${BASE_URL}${fileInfo.pathToFile}`}
                alt={fileInfo.filename || 'Image'}
                style={{ 
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
            
            <div style={{ 
              padding: '8px 12px', 
              background: 'white',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Space direction="vertical" size={1} style={{ width: '100%' }}>
                <Tooltip title={fileInfo.filename || 'Unknown'} placement="topLeft">
                  <Text style={{ fontSize: 14 }} ellipsis>
                    {truncateFileName(fileInfo.filename || 'Unknown')}
                  </Text>
                </Tooltip>
                <Space size={12} style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {fileInfo.h_size || 'Unknown size'}
                  </Text>
                  <Space>
                    <Tooltip title={t('fileAttachment.view')}>
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<EyeOutlined />} 
                        onClick={handleImageClick}
                      />
                    </Tooltip>
                    <Tooltip title={t('fileAttachment.download')}>
                      <Button 
                        type="primary" 
                        size="small" 
                        icon={isDownloading ? <LoadingOutlined /> : <DownloadOutlined />} 
                        onClick={handleFileDownload}
                        loading={isDownloading}
                        ghost
                      />
                    </Tooltip>
                  </Space>
                </Space>
                {isDownloading && downloadProgress > 0 && (
                  <Progress percent={downloadProgress} size="small" status="active" />
                )}
              </Space>
            </div>
          </div>
        </Card>
        
        {/* Image lightbox for full-size preview */}
        {showLightbox && (
          <ImageLightbox 
            src={`${BASE_URL}${fileInfo.pathToFile}`} 
            onClose={closeLightbox} 
            filename={fileInfo.filename || 'image.jpg'}
          />
        )}
      </>
    );
  }

  // For other file types
  return (
    <Card 
      className="file-attachment-card" 
      variant="borderless"
      styles={{ body: { padding: '12px' } }}
      style={{ borderRadius: 8, marginBottom: 8 }}
    >
      <Space size={12}>
        <div style={{ 
          fontSize: 24, 
          color: '#1890ff',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f0f7ff',
          borderRadius: 8
        }}>
          {getFileIcon(fileInfo.mimetype)}
        </div>
        <Space direction="vertical" size={0} style={{ flex: 1 }}>
          <Tooltip title={fileInfo.filename || 'Unknown'} placement="topLeft">
            <Text strong ellipsis>
              {truncateFileName(fileInfo.filename || 'Unknown')}
            </Text>
          </Tooltip>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {fileInfo.h_size || 'Unknown size'}
          </Text>
          {isDownloading && downloadProgress > 0 && (
            <Progress percent={downloadProgress} size="small" status="active" style={{ marginTop: 4 }} />
          )}
        </Space>
        <Tooltip title={t('fileAttachment.download')}>
          <Button 
            type="primary" 
            size="small" 
            icon={isDownloading ? <LoadingOutlined /> : <DownloadOutlined />} 
            onClick={handleFileDownload}
            loading={isDownloading}
          />
        </Tooltip>
      </Space>
    </Card>
  );
};

export default FileAttachment; 